import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createSalesReturn, getChallans } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { RotateCcw, Plus, Package, X, Search } from 'lucide-react';
import { FINISH_OPTIONS } from '@/lib/constants';

const defaultReturnItem = {
  item_code: '', description: '', category: 'other',
  size: '', grade: '', standard: '', finish: '',
  qty_pcs: '', weight_net_kg: '', weight_gross_kg: '',
  ctns_count: '', batch_no: '', heat_no: '',
};

export default function ReturnsPage() {
  const navigate = useNavigate();
  const [challans, setChallans] = useState([]);
  const [selectedChallan, setSelectedChallan] = useState('');
  const [notes, setNotes] = useState('');
  const [returnItems, setReturnItems] = useState([{ ...defaultReturnItem }]);
  const [loading, setLoading] = useState(false);
  const [challanSearch, setChallanSearch] = useState('');

  useEffect(() => {
    getChallans().then(r => setChallans(r.data)).catch(() => {});
  }, []);

  const filteredChallans = challans.filter(c =>
    c.challan_ref?.toLowerCase().includes(challanSearch.toLowerCase()) ||
    c.issued_to?.toLowerCase().includes(challanSearch.toLowerCase())
  );

  const addReturnItem = () => setReturnItems([...returnItems, { ...defaultReturnItem }]);

  const updateReturnItem = (idx, field, value) => {
    const updated = [...returnItems];
    updated[idx] = { ...updated[idx], [field]: value };
    setReturnItems(updated);
  };

  const removeReturnItem = (idx) => {
    if (returnItems.length <= 1) return;
    setReturnItems(returnItems.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!selectedChallan) return toast.error('Select original challan');
    const validItems = returnItems.filter(i => i.item_code && i.description);
    if (validItems.length === 0) return toast.error('Add at least one return item');

    setLoading(true);
    try {
      const data = {
        original_challan_ref: selectedChallan,
        items: validItems.map(i => ({
          ...i,
          qty_pcs: parseInt(i.qty_pcs) || 0,
          weight_net_kg: parseFloat(i.weight_net_kg) || 0,
          weight_gross_kg: parseFloat(i.weight_gross_kg) || 0,
          ctns_count: parseInt(i.ctns_count) || 0,
        })),
        notes,
      };
      const res = await createSalesReturn(data);
      toast.success(`Return pallet ${res.data.pallet_ref} created`);
      navigate(`/pallet/${res.data.pallet_ref}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create return');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="returns-page">
      <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
        <RotateCcw size={20} /> Sales Returns
      </h1>

      {/* Select Challan */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Original Dispatch Challan</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
            <Input value={challanSearch} onChange={e => setChallanSearch(e.target.value)}
              placeholder="Search challan ref or customer..." className="pl-9 h-9 text-sm" />
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {filteredChallans.slice(0, 10).map(c => (
              <button key={c.id}
                onClick={() => setSelectedChallan(c.challan_ref)}
                className={`w-full text-left p-2 rounded-lg text-xs transition-colors ${
                  selectedChallan === c.challan_ref ? 'bg-[#1a2fce]/10 border border-[#1a2fce]' : 'bg-gray-50 hover:bg-gray-100'
                }`}>
                <div className="font-mono font-bold">{c.challan_ref}</div>
                <div className="text-gray-500">{c.issued_to} · {c.date?.slice(0, 10)}</div>
              </button>
            ))}
          </div>
          {selectedChallan && <Badge className="text-xs" style={{ backgroundColor: '#1a2fce' }}>Selected: {selectedChallan}</Badge>}
        </CardContent>
      </Card>

      {/* Return Items */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Return Items</CardTitle>
            <Button onClick={addReturnItem} variant="outline" size="sm" className="h-7 text-xs">
              <Plus size={12} className="mr-1" /> Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {returnItems.map((item, idx) => (
            <div key={item.id || `return-item-${idx}`} className="p-3 border rounded-lg space-y-2 relative">
              {returnItems.length > 1 && (
                <button onClick={() => removeReturnItem(idx)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500">
                  <X size={14} />
                </button>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Item Code *</Label>
                  <Input value={item.item_code} onChange={e => updateReturnItem(idx, 'item_code', e.target.value)} className="h-8 text-xs" /></div>
                <div><Label className="text-xs">Finish</Label>
                  <Select value={item.finish} onValueChange={v => updateReturnItem(idx, 'finish', v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{FINISH_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div><Label className="text-xs">Description *</Label>
                <Input value={item.description} onChange={e => updateReturnItem(idx, 'description', e.target.value)} className="h-8 text-xs" /></div>
              <div className="grid grid-cols-4 gap-2">
                <div><Label className="text-xs">Size</Label><Input value={item.size} onChange={e => updateReturnItem(idx, 'size', e.target.value)} className="h-8 text-xs" /></div>
                <div><Label className="text-xs">Grade</Label><Input value={item.grade} onChange={e => updateReturnItem(idx, 'grade', e.target.value)} className="h-8 text-xs" /></div>
                <div><Label className="text-xs">Standard</Label><Input value={item.standard} onChange={e => updateReturnItem(idx, 'standard', e.target.value)} className="h-8 text-xs" /></div>
                <div><Label className="text-xs">Cartons</Label><Input type="number" value={item.ctns_count} onChange={e => updateReturnItem(idx, 'ctns_count', e.target.value)} className="h-8 text-xs" /></div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">Qty (pcs)</Label><Input type="number" value={item.qty_pcs} onChange={e => updateReturnItem(idx, 'qty_pcs', e.target.value)} className="h-8 text-xs" /></div>
                <div><Label className="text-xs">Net Wt (kg)</Label><Input type="number" step="0.01" value={item.weight_net_kg} onChange={e => updateReturnItem(idx, 'weight_net_kg', e.target.value)} className="h-8 text-xs" /></div>
                <div><Label className="text-xs">Gross Wt (kg)</Label><Input type="number" step="0.01" value={item.weight_gross_kg} onChange={e => updateReturnItem(idx, 'weight_gross_kg', e.target.value)} className="h-8 text-xs" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Batch No</Label><Input value={item.batch_no} onChange={e => updateReturnItem(idx, 'batch_no', e.target.value)} className="h-8 text-xs" /></div>
                <div><Label className="text-xs">Heat No</Label><Input value={item.heat_no} onChange={e => updateReturnItem(idx, 'heat_no', e.target.value)} className="h-8 text-xs" /></div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Notes & Submit */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div><Label className="text-xs">Return Notes</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Reason for return, condition, etc." className="h-9 text-sm" /></div>
          <Button onClick={handleSubmit} disabled={loading} className="w-full h-11 text-sm font-semibold" style={{ backgroundColor: '#1a2fce' }} data-testid="create-return">
            <RotateCcw size={14} className="mr-1" /> {loading ? 'Creating...' : 'Create Return Pallet'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
